/**
 * Authentication Context
 *
 * Provides authentication state and methods to the entire app
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize - check if user is already logged in
  useEffect(() => {
    const initAuth = async (retryCount = 0) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuthContext] Initializing...');
      }

      try {
        // Check if there's a valid session
        if (authService.isAuthenticated()) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[AuthContext] Token found, fetching user...');
          }

          const response = await authService.getCurrentUser();

          if (response.success && response.data) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[AuthContext] User loaded successfully');
            }
            setUser(response.data);
          } else {
            console.warn('[AuthContext] Failed to load user data');
          }
        }
      } catch (error) {
        console.error('[AuthContext] Initialization error:', error.message);

        // Retry logic for transient failures
        const isNetworkError = !error.response; // Network timeout, no response
        const is5xxError = error.response?.status >= 500; // Server error

        if ((isNetworkError || is5xxError) && retryCount < 2) {
          console.log(`[AuthContext] Retrying (attempt ${retryCount + 1}/2)...`);

          // Wait 1 second before retry
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Retry
          return initAuth(retryCount + 1);
        }

        // Clear invalid session only if it's a 401 (auth failure)
        if (error.response?.status === 401) {
          authService.clearToken();
        }
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initAuth();
  }, []);

  /**
   * Register a new user
   */
  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.register(userData);

      if (response.success && response.data) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.error?.message || 'Registration failed');
      }
    } catch (error) {
      console.error('[AuthContext] Register error:', error);

      // User-friendly error messages
      const isNetworkError = !error.response;
      const status = error.response?.status;

      let userMessage = 'Unable to create account';

      if (isNetworkError) {
        userMessage = 'Unable to connect. Please check your internet connection and try again.';
      } else if (status === 400) {
        userMessage = error.response?.data?.message || 'Please check your information and try again.';
      } else if (status === 409) {
        userMessage = 'An account with this email already exists.';
      } else if (status === 422) {
        userMessage = error.response?.data?.message || 'Invalid registration information. Please check your details.';
      } else if (status >= 500) {
        userMessage = 'Our servers are experiencing issues. Please try again in a moment.';
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.message) {
        userMessage = error.message;
      }

      setError(userMessage);
      return { success: false, error: userMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.login(credentials);

      if (response.success && response.data) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      } else {
        throw new Error(response.error?.message || 'Login failed');
      }
    } catch (error) {
      console.error('[AuthContext] Login error:', error);

      // User-friendly error messages
      const isNetworkError = !error.response;
      const status = error.response?.status;

      let userMessage = 'Unable to log in';

      if (isNetworkError) {
        userMessage = 'Unable to connect. Please check your internet connection and try again.';
      } else if (status === 401) {
        userMessage = 'Invalid email or password. Please try again.';
      } else if (status === 403) {
        userMessage = 'Your account has been locked. Please contact support.';
      } else if (status === 429) {
        userMessage = 'Too many login attempts. Please try again later.';
      } else if (status >= 500) {
        userMessage = 'Our servers are experiencing issues. Please try again in a moment.';
      } else if (error.response?.data?.message) {
        userMessage = error.response.data.message;
      } else if (error.message) {
        userMessage = error.message;
      }

      setError(userMessage);
      return { success: false, error: userMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login with Google
   */
  const loginWithGoogle = useCallback(async (idToken) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.loginWithGoogle(idToken);

      // authService returns { success, user, message }
      if (response.success && response.user) {
        setUser(response.user);
        return { success: true, user: response.user };
      } else {
        throw new Error(response.error?.message || 'Google login failed');
      }
    } catch (error) {
      console.error('[AuthContext] Google login error:', error);
      const errorMessage = error.message || 'Google login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setError(null);

      // Revoke Google session hint so next sign-in shows account picker
      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }

      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      // Still clear local state even if API call fails
      setUser(null);
      return { success: true };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        return { success: true, user: response.data };
      } else {
        throw new Error('Failed to refresh user');
      }
    } catch (error) {
      console.error('[AuthContext] Refresh user error:', error);
      // If refresh fails, logout
      authService.clearToken();
      setUser(null);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    // State
    user,
    loading,
    error,
    initialized,
    isAuthenticated: !!user,

    // Methods
    register,
    login,
    loginWithGoogle,
    logout,
    refreshUser,
    clearError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
